import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Layout, List, Input, Button, message, Typography, Avatar, Empty, Spin, Modal } from 'antd';
import { SendOutlined, MessageOutlined, UserOutlined, CheckCircleOutlined, CheckOutlined, SaveOutlined, ArrowLeftOutlined, FileTextOutlined, MenuOutlined, ReloadOutlined, CloseCircleOutlined, ClockCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import { WAChatDTO, WAMessageDTO } from '../types/auth';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import '../styles/responsive.css';

dayjs.locale('ru');

const { Content, Sider } = Layout;
const { Text } = Typography;
const { TextArea } = Input;

// Компонент иконки наушников
const HeadphonesIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    width="16"
    height="16"
  >
    <path d="M12 1C6.48 1 2 5.48 2 11v7c0 1.1.9 2 2 2h4v-8H4v-1c0-4.42 3.58-8 8-8s8 3.58 8 8v1h-4v8h4c1.1 0 2-.9 2-2v-7c0-5.52-4.48-10-10-10z" />
  </svg>
);

// Функция для получения ID чата (используем phone как ID)
const getChatId = (chat: WAChatDTO): string => {
  return chat.phone;
};

const CHATS_PAGE_SIZE = 30;

const WAChats: React.FC = () => {
  const [chats, setChats] = useState<WAChatDTO[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<WAMessageDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isNoteModalVisible, setIsNoteModalVisible] = useState(false);
  const [showChatsList, setShowChatsList] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatsListRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);
  const loadedChatPagesRef = useRef<Set<string>>(new Set());
  const messagesLoadingRef = useRef(false);
  const navigate = useNavigate();

  const getChatPageKey = (page: number, query: string) => `${query}:${page}`;

  const canLoadMoreChats = useCallback(() => {
    if (loading || loadingMore || !hasMore || loadingMoreRef.current) {
      return false;
    }
    if (isMobile && selectedChatId && !showChatsList) {
      return false;
    }
    const container = chatsListRef.current;
    return Boolean(container && container.clientHeight > 0);
  }, [loading, loadingMore, hasMore, isMobile, selectedChatId, showChatsList]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // На десктопе всегда показываем список чатов
      if (!mobile) {
        setShowChatsList(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadChats = useCallback(async (page: number, append: boolean, query: string) => {
    const pageKey = getChatPageKey(page, query);
    if (loadedChatPagesRef.current.has(pageKey)) {
      return;
    }

    if (page === 1 && !append) {
      loadedChatPagesRef.current.clear();
    }

    if (page === 1) {
      setLoading(true);
    } else {
      if (loadingMoreRef.current) {
        return;
      }
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    loadedChatPagesRef.current.add(pageKey);

    try {
      const data = await apiService.getWAChats(page, CHATS_PAGE_SIZE, query || undefined);
      setChats((prev) => (append ? [...prev, ...data.content] : data.content));
      setCurrentPage(data.currentPage);
      setHasMore(data.currentPage < data.totalPages);
    } catch (error: any) {
      loadedChatPagesRef.current.delete(pageKey);
      console.error('Error loading chats:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view WA chats.');
      } else {
        message.error('Failed to load chats');
      }
    } finally {
      if (page === 1) {
        setLoading(false);
      } else {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [navigate]);

  useEffect(() => {
    loadedChatPagesRef.current.clear();
    loadChats(1, false, searchQuery);
  }, [searchQuery, loadChats]);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
    chatsListRef.current?.scrollTo(0, 0);
  };

  const handleChatsScroll = useCallback(() => {
    if (!canLoadMoreChats()) {
      return;
    }

    const container = chatsListRef.current;
    if (!container) {
      return;
    }

    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceToBottom <= 80) {
      loadChats(currentPage + 1, true, searchQuery);
    }
  }, [canLoadMoreChats, currentPage, loadChats, searchQuery]);

  useEffect(() => {
    if (!canLoadMoreChats()) {
      return;
    }

    const container = chatsListRef.current;
    if (!container) {
      return;
    }

    if (container.scrollHeight <= container.clientHeight + 1) {
      loadChats(currentPage + 1, true, searchQuery);
    }
  }, [canLoadMoreChats, chats.length, currentPage, loadChats, searchQuery]);

  const loadMessages = useCallback(async (chatId: string) => {
    if (messagesLoadingRef.current) {
      return;
    }

    messagesLoadingRef.current = true;
    setMessagesLoading(true);
    setMessages([]);
    try {
      const data = await apiService.getWAChatMessages(chatId);
      setMessages(data);
    } catch (error: any) {
      console.error('Error loading messages:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        message.error(error.response?.data?.msg || 'Failed to load messages');
      }
    } finally {
      messagesLoadingRef.current = false;
      setMessagesLoading(false);
    }
  }, [navigate]);

  const saveNote = async (phone: string, noteText: string) => {
    setSavingNote(true);
    try {
      await apiService.updateWANote(phone, { note: noteText });
      console.log('Note saved successfully');
    } catch (error: any) {
      console.error('Error saving note:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        message.error(error.response?.data?.msg || 'Failed to save note');
      }
    } finally {
      setSavingNote(false);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedChatId) {
      return;
    }
    await saveNote(selectedChatId, note);
    
    // Обновляем заметку в памяти (в списке чатов)
    setChats(prevChats => 
      prevChats.map(chat => {
        if (getChatId(chat) === selectedChatId) {
          return { ...chat, note };
        }
        return chat;
      })
    );
    
    message.success('Note saved');
  };

  useEffect(() => {
    if (!selectedChatId) {
      setMessages([]);
      setNote('');
      if (isMobile) {
        setShowChatsList(true);
      }
      return;
    }

    loadMessages(selectedChatId);

    if (isMobile) {
      setShowChatsList(false);
    }
  }, [selectedChatId, isMobile, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!selectedChatId || !messageText.trim()) {
      return;
    }

    setSending(true);
    try {
      await apiService.sendWAMessage(selectedChatId, {
        text: messageText.trim()
      });
      setMessageText('');
      // Reload messages after sending
      await loadMessages(selectedChatId);
      message.success('Message sent');
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        message.error(error.response?.data?.msg || 'Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return dayjs(dateString).format('HH:mm');
  };

  const formatDateRelative = (dateString?: string) => {
    if (!dateString) return '';
    const date = dayjs(dateString);
    const now = dayjs();
    const diffDays = now.diff(date, 'day');
    
    if (diffDays === 0) {
      return `Сегодня ${date.format('HH:mm')}`;
    } else if (diffDays === 1) {
      return `Вчера ${date.format('HH:mm')}`;
    } else if (diffDays < 7) {
      return date.format('dddd HH:mm');
    } else {
      return date.format('DD.MM.YYYY HH:mm');
    }
  };

  const renderMessageStatus = (msg: WAMessageDTO, isFromSupport: boolean) => {
    // Показываем статус только для сообщений от саппорта
    if (!isFromSupport) {
      return null;
    }

    // Если есть state, используем его, иначе используем старую логику isDelivered
    if (!msg.state) {
      return null;
    }

    // Не показываем статус NO_NEEDED
    if (msg.state === 'NO_NEEDED') {
      return null;
    }

    const statusConfig: Record<string, { text: string; icon: React.ReactNode; color: string }> = {
      TO_SENT: { 
        text: 'в процессе', 
        icon: <ClockCircleOutlined style={{ fontSize: '13px' }} />, 
        color: '#faad14' 
      },
      SEND: { 
        text: '', 
        icon: <CheckCircleOutlined style={{ fontSize: '13px' }} />, 
        color: '#52c41a' 
      },
      ERROR: { 
        text: 'ошибка', 
        icon: <CloseCircleOutlined style={{ fontSize: '13px' }} />, 
        color: '#ff4d4f' 
      }
    };

    const config = statusConfig[msg.state];
    if (!config) return null;

    return (
      <div
        style={{
          fontSize: '10px',
          opacity: 0.9,
          color: config.color,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '2px',
          fontWeight: 500
        }}
      >
        {config.icon}
        {config.text && <span>{config.text}</span>}
      </div>
    );
  };

  const handleBackToChats = () => {
    if (isMobile) {
      setSelectedChatId(null);
      setShowChatsList(true);
    } else {
      setSelectedChatId(null);
    }
  };

  return (
    <Layout style={{ height: '100%', background: '#fff', overflow: 'hidden' }} className="wa-chats-layout">
      <Sider
        width={300}
        className="wa-chats-sider desktop-sider"
        style={{
          background: '#fff',
          borderRight: '1px solid #f0f0f0',
          position: isMobile ? 'fixed' : 'relative',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: isMobile ? 1000 : 1,
          transform: isMobile && !showChatsList && selectedChatId ? 'translateX(-100%)' : 'translateX(0)',
          transition: 'transform 0.3s ease'
        }}
      >
        <div className="wa-chats-sider-inner">
          <div className="wa-chats-sider-header">
            <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: '12px' }}>WhatsApp Chats</Text>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Phone number"
                allowClear
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                Search
              </Button>
            </div>
          </div>
          <div
            ref={chatsListRef}
            className="wa-chats-list-scroll"
            onScroll={handleChatsScroll}
          >
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <Spin />
            </div>
          ) : chats.length === 0 ? (
            <Empty
              description={searchQuery ? 'No chats match your search' : 'No chats found'}
              style={{ marginTop: '40px' }}
            />
          ) : (
            <>
              <List
                dataSource={chats}
                renderItem={(chat) => {
                  const chatId = getChatId(chat);

                  return (
                    <List.Item
                      style={{
                        cursor: 'pointer',
                        padding: '12px 16px',
                        backgroundColor: selectedChatId === chatId ? '#e6f7ff' : '#fff',
                        borderBottom: '1px solid #f0f0f0'
                      }}
                      onClick={() => {
                        if (chatId) {
                          setSelectedChatId(chatId);
                          setNote(chat.note || '');
                        } else {
                          message.error('Ошибка: не удалось определить ID чата');
                        }
                      }}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<MessageOutlined />} />}
                        title={<Text strong>{chat.phone}</Text>}
                        description={
                          <div>
                            {chat.lastMessageAt ? (
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {formatDateRelative(chat.lastMessageAt)}
                              </Text>
                            ) : (
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                Нет сообщений
                              </Text>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
              {hasMore && loadingMore && (
                <div style={{ padding: '12px', textAlign: 'center' }}>
                  <Spin size="small" />
                </div>
              )}
            </>
          )}
        </div>
        </div>
      </Sider>
      {/* Overlay для мобильных устройств, когда список чатов открыт поверх чата */}
      {isMobile && showChatsList && selectedChatId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            zIndex: 999,
          }}
          onClick={() => setShowChatsList(false)}
        />
      )}
      <Content style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {selectedChatId ? (
          <Layout style={{ height: '100%', background: '#fff', display: 'flex', flexDirection: 'row', overflow: 'hidden' }} className="wa-chats-content-layout">
            <Content style={{ display: 'flex', flexDirection: 'column', height: '100%', width: isMobile ? '100%' : '75%', minWidth: 0, overflow: 'hidden' }}>
              <div
                style={{
                  padding: isMobile ? '12px' : '16px',
                  borderBottom: '1px solid #f0f0f0',
                  background: '#fafafa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                {isMobile && (
                  <Button
                    type="text"
                    icon={showChatsList ? <ArrowLeftOutlined /> : <MenuOutlined />}
                    onClick={() => {
                      if (showChatsList) {
                        handleBackToChats();
                      } else {
                        setShowChatsList(true);
                      }
                    }}
                    style={{ flexShrink: 0 }}
                  />
                )}
                <Text strong style={{ fontSize: isMobile ? '14px' : '16px', flex: 1 }}>
                  {chats.find(c => getChatId(c) === selectedChatId)?.phone || 'Chat'}
                </Text>
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    if (selectedChatId) {
                      loadMessages(selectedChatId);
                    }
                  }}
                  loading={messagesLoading}
                  style={{ flexShrink: 0 }}
                  title="Обновить сообщения"
                />
                {isMobile && (
                  <Button
                    type="text"
                    icon={<FileTextOutlined />}
                    onClick={() => setIsNoteModalVisible(true)}
                    style={{ flexShrink: 0 }}
                  />
                )}
              </div>
            <div
              ref={messagesContainerRef}
              style={{
                flex: 1,
                overflow: 'auto',
                minHeight: 0,
                padding: isMobile ? '12px' : '16px',
                background: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end'
              }}
            >
              {messagesLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin />
                </div>
              ) : messages.length === 0 ? (
                <Empty description="No messages" />
              ) : (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}
                >
                  {[...messages].reverse().map((msg) => {
                    // Определяем, является ли сообщение от клиента
                    // fromClient может быть undefined (тогда используем fromMe)
                    const isFromClient = msg.fromClient !== undefined ? msg.fromClient : !msg.fromMe;
                    const isFromSupport = !isFromClient;
                    
                    return (
                      <div
                        key={msg.id}
                        style={{
                          display: 'flex',
                          justifyContent: isFromSupport ? 'flex-end' : 'flex-start',
                          alignItems: 'flex-end',
                          gap: '8px'
                        }}
                      >
                        {isFromClient && (
                          <Avatar
                            icon={<UserOutlined />}
                            size="small"
                            style={{
                              backgroundColor: '#87d068',
                              flexShrink: 0
                            }}
                          />
                        )}
                        <div
                          style={{
                            maxWidth: isMobile ? '85%' : '70%',
                            padding: isMobile ? '6px 10px' : '8px 12px',
                            borderRadius: '12px',
                            background: isFromSupport ? '#1890ff' : '#fff',
                            color: isFromSupport ? '#fff' : '#000',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                          }}
                        >
                          <div style={{ wordBreak: 'break-word' }}>{msg.text}</div>
                          <div
                            style={{
                              fontSize: '11px',
                              opacity: 0.8,
                              textAlign: 'right',
                              alignSelf: 'flex-end',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              marginTop: '4px'
                            }}
                          >
                            {formatDate(msg.dateTime)}
                            {/* Показываем старую логику только если нет state */}
                            {isFromSupport && !msg.state && (
                              <>
                                {msg.isDelivered ? (
                                  <CheckCircleOutlined style={{ fontSize: '12px' }} />
                                ) : (
                                  <CheckOutlined style={{ fontSize: '12px' }} />
                                )}
                              </>
                            )}
                          </div>
                          {/* Статус сообщения под временем */}
                          {renderMessageStatus(msg, isFromSupport)}
                        </div>
                        {isFromSupport && (
                          <Avatar
                            icon={<HeadphonesIcon />}
                            size="small"
                            style={{
                              backgroundColor: '#1890ff',
                              flexShrink: 0
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
            <div
              style={{
                padding: isMobile ? '12px' : '16px',
                borderTop: '1px solid #f0f0f0',
                background: '#fff'
              }}
            >
              <div style={{ display: 'flex', gap: isMobile ? '6px' : '8px', alignItems: 'flex-end' }}>
                <TextArea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  autoSize={{ minRows: isMobile ? 1 : 2, maxRows: 4 }}
                  disabled={sending}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSendMessage}
                  loading={sending}
                  disabled={!messageText.trim()}
                  style={{ flexShrink: 0 }}
                >
                  {!isMobile && 'Send'}
                </Button>
              </div>
            </div>
            </Content>
            {!isMobile && (
              <Sider
                width="25%"
                className="wa-chats-note-sider"
                style={{
                  background: '#fff',
                  borderLeft: '1px solid #f0f0f0',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minWidth: 0,
                  overflow: 'hidden'
                }}
              >
                <div style={{ padding: '16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong style={{ fontSize: '16px' }}>Note</Text>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveNote}
                    loading={savingNote}
                    disabled={!selectedChatId}
                    size="small"
                  >
                    Update
                  </Button>
                </div>
                <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <TextArea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Enter note..."
                    autoSize={{ minRows: 10 }}
                    style={{ flex: 1 }}
                    disabled={savingNote}
                  />
                </div>
              </Sider>
            )}
          </Layout>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              flexDirection: 'column'
            }}
          >
            <MessageOutlined style={{ fontSize: isMobile ? '48px' : '64px', color: '#d9d9d9', marginBottom: '16px' }} />
            <Text type="secondary" style={{ fontSize: isMobile ? '14px' : '16px' }}>Select a chat to start messaging</Text>
          </div>
        )}
      </Content>

      {/* Модальное окно для заметок на мобильных */}
      <Modal
        title="Note"
        open={isNoteModalVisible}
        onCancel={() => setIsNoteModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsNoteModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            onClick={async () => {
              await handleSaveNote();
              setIsNoteModalVisible(false);
            }}
            loading={savingNote}
            disabled={!selectedChatId}
          >
            Save
          </Button>
        ]}
        width={isMobile ? '90%' : 600}
      >
        <TextArea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Enter note..."
          autoSize={{ minRows: 8, maxRows: 15 }}
          disabled={savingNote}
        />
      </Modal>
    </Layout>
  );
};

export default WAChats;

